import { useEffect, useMemo, useState } from 'react';
import customFetch from '../../../api/customFetch';
import {
  Section,
  SectionTitle,
  SectionHeader,
  LoadingContainer,
  Spinner,
  LoadingText,
  Alert,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
} from '../../../pages/SectorManager/Dashboard/styles';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

const SECTION_LABELS = {
  'part-a': 'א: סביבת עבודה',
  'part-b': 'ב: עבודת צוות',
  'part-c': 'ג: נראות והערכה',
  'part-d': 'ד: מנהל ישיר',
  'part-e': 'ה: הנהלה בכירה',
  'part-f': 'ו: פיתוח מקצועי',
  'part-g': 'ז: איזון בית-עבודה',
  'part-h': 'ח: נאמנות ושביעות רצון',
};

export default function ClimateSurveyChartCard({
  title = '📊 סקר אקלים – ממוצעים לפי קטגוריה',
  subtitle = 'סולם 1–5 | מציג ממוצע לכל חלק (A–H)',
  defaultYear = new Date().getFullYear(),
  year: controlledYear, // אופציונלי: אם תרצה לשלוט מבחוץ
  onYearChange, // אופציונלי
  endpoint = '/survey/analytics/averages', // אפשר להחליף בקלות בעתיד
  query = {}, // אופציונלי: { sector: 'IT' } וכו'
}) {
  const [internalYear, setInternalYear] = useState(defaultYear);
  const year = controlledYear ?? internalYear;

  const setYear = (val) => {
    if (onYearChange) onYearChange(val);
    if (controlledYear == null) setInternalYear(val);
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ year: String(year) });
    Object.entries(query || {}).forEach(([k, v]) => {
      if (v == null || v === '') return;
      params.set(k, String(v));
    });
    return params.toString();
  }, [year, query]);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setErr(null);
        
        const { data } = await customFetch.get(`${endpoint}?${queryString}`);
        setRows(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        setErr(
          e?.response?.data?.message || e.message || 'שגיאה בטעינת נתוני סקר'
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [endpoint, queryString]);

  const chartData = useMemo(
    () =>
      rows.map((r) => ({
        sectionId: r.sectionId,
        label: SECTION_LABELS[r.sectionId] || r.sectionId,
        avg: typeof r.avg === 'number' ? r.avg : 0,
        count: r.count || 0,
      })),
    [rows]
  );

  return (
    <Section style={{ marginTop: 0 }}>
      <SectionHeader>
        <div>
          <SectionTitle>{title}</SectionTitle>
          <div style={{ color: '#666', fontSize: '0.85rem', marginTop: 6 }}>
            {subtitle}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '0.9rem', color: '#444' }}>שנה</span>
          <input
            type='number'
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{
              width: 90,
              padding: '10px 12px',
              borderRadius: '10px',
              border: '1px solid #e9ecef',
              fontSize: '0.95rem',
            }}
          />
        </div>
      </SectionHeader>

      <div
        style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: 16,
          padding: 16,
        }}
      >
        {loading ? (
          <LoadingContainer style={{ minHeight: 'auto', padding: '40px 0' }}>
            <Spinner />
            <LoadingText>טוען נתוני סקר...</LoadingText>
          </LoadingContainer>
        ) : err ? (
          <Alert type='error' style={{ margin: 0 }}>
            {err}
          </Alert>
        ) : chartData.length === 0 ? (
          <EmptyState style={{ margin: 0 }}>
            <EmptyIcon>📭</EmptyIcon>
            <EmptyTitle>אין נתונים לשנה {year}</EmptyTitle>
            <EmptyText>כשיהיו תשובות, הגרף יתמלא כאן</EmptyText>
          </EmptyState>
        ) : (
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='label' interval={0} tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 5]} />
                <Tooltip
                  labelFormatter={(label) => `קטגוריה: ${label}`}
                  formatter={(value) => [value, 'ממוצע']}
                />
                <Bar dataKey='avg' />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Section>
  );
}
